import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import { FiMenu } from 'react-icons/fi'
import apiInstance from '../../../../../Utils/api'
import { Delete } from '../../web_api/api'
import BranchButton from '../../branch/shared/BranchButton'
import BranchSearchInput from '../../branch/shared/BranchSearchInput'
import BranchImageThumb from '../../branch/shared/BranchImageThumb'
import BranchActionIcons from '../../branch/shared/BranchActionIcons'
import BranchPagination from '../../branch/shared/BranchPagination'
import BranchEmptyState from '../../branch/shared/BranchEmptyState'
import BranchSelect from '../../branch/shared/BranchSelect'
import BranchBreadcrumb from '../../branch/shared/BranchBreadcrumb'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import '../../branch/branch.css'
import '../staff.css'

const roleOptions = ['', 'Founder', 'Doctor', 'Both']

export default function StaffList() {
  const [list, setList] = useState([])
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [activeFilter, setActiveFilter] = useState('')
  const [sortMode, setSortMode] = useState(false)
  const [sortItems, setSortItems] = useState([])
  const [draggedIdx, setDraggedIdx] = useState(null)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)
  const abortRef = useRef(null)
  const fetchIdRef = useRef(0)
  const perPage = 10

  const fetchList = async (p, kw) => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()
    const id = ++fetchIdRef.current

    setLoading(true)
    try {
      const params = { keyword: kw || '', page: p, limit: perPage }
      if (roleFilter) params.role = roleFilter
      if (activeFilter === 'active') params.isActive = true
      if (activeFilter === 'inactive') params.isActive = false
      const res = await apiInstance.get('staffs', { params, signal: abortRef.current.signal })
      if (id !== fetchIdRef.current) return
      const data = res.data
      setList(data.list || [])
      setTotalCount(data._metadata?.total_count || 0)
      setPageCount(data._metadata?.page_count || 0)
    } catch (err) {
      if (err?.name === 'CanceledError') return
      console.error(err)
    } finally {
      if (id === fetchIdRef.current) setLoading(false)
    }
  }

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchList(1, keyword)
      setPage(1)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [keyword])

  useEffect(() => {
    fetchList(1, keyword)
    setPage(1)
    setSortMode(false)
  }, [roleFilter, activeFilter])

  useEffect(() => {
    fetchList(page, keyword)
  }, [page])

  const enterSortMode = async () => {
    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()
    const id = ++fetchIdRef.current

    setSortMode(true)
    setLoading(true)
    try {
      const params = { keyword: keyword || '', page: 1, limit: 100, isActive: true }
      if (roleFilter) params.role = roleFilter
      const res = await apiInstance.get('staffs', { params, signal: abortRef.current.signal })
      if (id !== fetchIdRef.current) return
      const data = res.data
      setList(data.list || [])
      setSortItems(data.list || [])
    } catch (err) {
      if (err?.name === 'CanceledError') return
      console.error(err)
    } finally {
      if (id === fetchIdRef.current) setLoading(false)
    }
  }

  const cancelSort = () => {
    setSortMode(false)
    setSortItems([])
    setDraggedIdx(null)
    fetchList(1, keyword)
    setPage(1)
  }

  const saveSort = async () => {
    setSaving(true)
    try {
      const items = sortItems.map((s, i) => ({ _id: s._id, displayOrder: i + 1 }))
      await apiInstance.put('staffs/sort', { items })
      Swal.fire({ title: 'Sort order updated!', icon: 'success', showConfirmButton: false, timer: 1500 })
      setSortMode(false)
      fetchList(1, keyword)
      setPage(1)
    } catch (err) {
      Swal.fire({ title: 'Failed to save sort order', icon: 'error', showConfirmButton: false, timer: 2000 })
    } finally {
      setSaving(false)
    }
  }

  const handleDragStart = (idx) => {
    setDraggedIdx(idx)
  }

  const handleDragOver = (e, idx) => {
    e.preventDefault()
    if (draggedIdx === null || draggedIdx === idx) return
    const updated = [...sortItems]
    const [moved] = updated.splice(draggedIdx, 1)
    updated.splice(idx, 0, moved)
    setSortItems(updated)
    setDraggedIdx(idx)
  }

  const handleDragEnd = () => {
    setDraggedIdx(null)
  }

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This staff profile will be deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#db2777',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    })
    if (!result.isConfirmed) return
    await Delete('/staff/', id)
    fetchList(page, keyword)
  }

  const handleActivate = async (id) => {
    try {
      await apiInstance.post('staff/' + id)
      fetchList(page, keyword)
    } catch (err) {
      console.error(err)
    }
  }

  const handleToggle = async (item) => {
    const action = item.isActive ? 'deactivate' : 'activate'
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `This staff profile will be ${action}d.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#db2777',
      cancelButtonColor: '#64748b',
      confirmButtonText: `Yes, ${action}`,
      cancelButtonText: 'Cancel'
    })
    if (!result.isConfirmed) return
    try {
      await apiInstance.post(`staff/${item._id}/${action}`)
      fetchList(page, keyword)
    } catch (err) {
      console.error(err)
    }
  }

  const items = sortMode ? sortItems : list
  const showSortBtn = activeFilter === 'active' && !sortMode

  return (
    <div className='branch-page'>
      <BranchBreadcrumb items={[
        { label: 'Staff', to: '/staff' },
        { label: 'Staff List' }
      ]} />

      <div className='branch-card'>
        <div className='branch-card-header'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className='branch-filter-group'>
              <button className={'branch-filter-btn' + (activeFilter === '' ? ' active' : '')} onClick={() => setActiveFilter('')}>All</button>
              <button className={'branch-filter-btn' + (activeFilter === 'active' ? ' active' : '')} onClick={() => setActiveFilter('active')}>Active</button>
              <button className={'branch-filter-btn' + (activeFilter === 'inactive' ? ' active' : '')} onClick={() => setActiveFilter('inactive')}>Inactive</button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {sortMode ? (
              <>
                <BranchButton onClick={cancelSort} variant='outline'>Cancel</BranchButton>
                <BranchButton onClick={saveSort} loading={saving}>Save Order</BranchButton>
              </>
            ) : (
              <>
                <BranchSelect
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  options={[
                    { value: '', label: 'All Roles' },
                    ...roleOptions.filter(Boolean).map(r => ({ value: r, label: r }))
                  ]}
                  width={140}
                />
                <BranchSearchInput
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (debounceRef.current) clearTimeout(debounceRef.current)
                      fetchList(1, keyword)
                      setPage(1)
                    }
                  }}
                  placeholder='Search staff...'
                />
                <BranchButton to='/staff-create'>+ Add</BranchButton>
                {showSortBtn && <BranchButton onClick={enterSortMode} variant='outline'>Reorder</BranchButton>}
              </>
            )}
          </div>
        </div>

        <div className='branch-card-body' style={{ padding: 0 }}>
          {loading ? (
            <div className='branch-loader'>
              <div className='branch-loader-spinner' />
              <div className='branch-loader-text'>Loading staff...</div>
            </div>
          ) : items.length === 0 ? (
            <BranchEmptyState title='No staff found' actionTo='/staff-create' />
          ) : (
            <>
              <div className='branch-table-wrapper'>
                <table className='branch-table'>
                  <thead>
                    <tr>
                      {sortMode && <th style={{ width: '4%' }} />}
                      <th style={{ width: sortMode ? '4%' : '5%' }}>#</th>
                      <th style={{ width: '8%' }}>Photo</th>
                      <th style={{ width: '20%' }}>Name</th>
                      <th style={{ width: '12%' }}>Role</th>
                      <th style={{ width: '22%' }}>Specialties</th>
                      <th style={{ width: '8%' }}>Status</th>
                      <th style={{ width: '12%' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => (
                      <tr
                        key={item._id}
                        draggable={sortMode}
                        onDragStart={() => sortMode && handleDragStart(i)}
                        onDragOver={(e) => sortMode && handleDragOver(e, i)}
                        onDragEnd={handleDragEnd}
                        className={draggedIdx === i ? 'branch-dragging' : ''}
                        style={{ opacity: draggedIdx === i ? 0.5 : 1 }}
                      >
                        {sortMode && (
                          <td>
                            <span style={{ cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--branch-text-muted)' }}>
                              <FiMenu size={16} />
                            </span>
                          </td>
                        )}
                        <td>{(page - 1) * perPage + i + 1}</td>
                        <td>
                          <PhotoProvider>
                            <PhotoView src={item.imageUrl}>
                              <BranchImageThumb src={item.imageUrl} alt={item.name} />
                            </PhotoView>
                          </PhotoProvider>
                        </td>
                        <td>
                          <Link to={'/staff-detail/' + item._id} style={{ color: 'inherit', textDecoration: 'none' }}>
                            <span className='staff-title-label'>{item.title} </span>
                            <span className='staff-title-name'>{item.name}</span>
                          </Link>
                        </td>
                        <td>
                          {item.role === 'Both' ? (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              <span className='staff-role-badge staff-role-Founder'>Founder</span>
                              <span className='staff-role-badge staff-role-Doctor'>Doctor</span>
                            </div>
                          ) : (
                            <span className={`staff-role-badge staff-role-${item.role}`}>{item.role}</span>
                          )}
                        </td>
                        <td title={item.specialties?.join(', ') || '-'}>
                          {item.specialties?.length > 0
                            ? item.specialties.join(', ')
                            : '-'}
                        </td>
                        <td>
                          {!item.isDeleted && (
                            <label className='branch-toggle-sm' title={item.isActive ? 'Deactivate' : 'Activate'}>
                              <input
                                type='checkbox'
                                checked={!!item.isActive}
                                onChange={() => handleToggle(item)}
                              />
                              <span className='branch-toggle-slider' />
                            </label>
                          )}
                        </td>
                        <td>
                          <BranchActionIcons
                            branch={item}
                            onDelete={handleDelete}
                            onRestore={handleActivate}
                            editPath='/staff-update/'
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!sortMode && (
                <BranchPagination
                  page={page}
                  pageCount={pageCount}
                  totalCount={totalCount}
                  perPage={perPage}
                  onChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
